import { useState, type FormEvent } from "react";
import type { AdminUserRow, UserRole } from "@omts/shared";
import { adminApi, fieldErrorsOf } from "../../api";
import { useAuth } from "../../auth";
import { Modal } from "../../components/Modal";
import { ConfirmActions, Field, TelegramTag, errMsg, useList } from "./common";

const ROLE_LABELS: Record<UserRole, string> = { logist: "Логист", admin: "Администратор" };
const MIN_PASSWORD = 8;

function RoleSelect({ value, onChange }: { value: UserRole; onChange: (r: UserRole) => void }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value as UserRole)}>
      <option value="logist">{ROLE_LABELS.logist}</option>
      <option value="admin">{ROLE_LABELS.admin}</option>
    </select>
  );
}

type AddForm = { email: string; fullName: string; role: UserRole; password: string };
type AddErrors = Partial<Record<keyof AddForm | "form", string>>;

function AddUserModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState<AddForm>({ email: "", fullName: "", role: "logist", password: "" });
  const [errors, setErrors] = useState<AddErrors>({});
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof AddForm>(key: K, value: AddForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const v: AddErrors = {};
    if (!form.fullName.trim()) v.fullName = "Обязательное поле";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) v.email = "Некорректный e-mail";
    if (form.password.length < MIN_PASSWORD) v.password = `Не короче ${MIN_PASSWORD} символов`;
    if (Object.keys(v).length) return setErrors(v);
    setBusy(true);
    try {
      await adminApi.createUser({ ...form, email: form.email.trim(), fullName: form.fullName.trim() });
      onDone();
    } catch (err) {
      setErrors({ ...fieldErrorsOf(err), form: errMsg(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Новый пользователь" onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} noValidate>
        <Field label="ФИО" required error={errors.fullName}>
          <input value={form.fullName} onChange={(e) => set("fullName", e.target.value)} autoFocus />
        </Field>
        <Field label="E-mail (логин)" required error={errors.email}>
          <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Роль" required>
          <RoleSelect value={form.role} onChange={(r) => set("role", r)} />
        </Field>
        <Field label="Пароль" required error={errors.password} hint={`Не короче ${MIN_PASSWORD} символов; сообщите его сотруднику лично`}>
          <input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} autoComplete="new-password" />
        </Field>
        {errors.form && <p className="error">{errors.form}</p>}
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? "Сохранение…" : "Создать"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditUserModal({ user, isSelf, onClose, onDone }: { user: AdminUserRow; isSelf: boolean; onClose: () => void; onDone: () => void }) {
  const [fullName, setFullName] = useState(user.fullName);
  const [role, setRole] = useState<UserRole>(user.role);
  const [isActive, setIsActive] = useState(user.isActive);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) return setError("Укажите ФИО");
    setBusy(true);
    setError(null);
    try {
      await adminApi.updateUser(user.id, { fullName: fullName.trim(), role, isActive });
      onDone();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={user.email} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} noValidate>
        <Field label="ФИО" required>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} autoFocus />
        </Field>
        <Field label="Роль" hint={isSelf ? "Свою роль администратора снять нельзя" : undefined}>
          {isSelf ? <input value={ROLE_LABELS[role]} disabled /> : <RoleSelect value={role} onChange={setRole} />}
        </Field>
        <label className="field check">
          <span className="row">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} disabled={isSelf} />
            Активен (может входить в портал)
          </span>
          {isSelf && <small className="hint">Себя деактивировать нельзя</small>}
        </label>
        {error && <p className="error">{error}</p>}
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? "Сохранение…" : "Сохранить"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function PasswordModal({ user, onClose, onDone }: { user: AdminUserRow; onClose: () => void; onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) return setError(`Пароль — не короче ${MIN_PASSWORD} символов`);
    setBusy(true);
    setError(null);
    try {
      await adminApi.updateUser(user.id, { password });
      onDone();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Новый пароль — ${user.fullName}`} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} noValidate>
        <Field label="Новый пароль" required error={error ?? undefined} hint="Сообщите его сотруднику лично; старый пароль перестанет действовать">
          <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" autoFocus />
        </Field>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? "Сохранение…" : "Сменить пароль"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

type Dialog = { kind: "add" } | { kind: "edit"; user: AdminUserRow } | { kind: "password"; user: AdminUserRow } | { kind: "unlink"; user: AdminUserRow } | null;

export function UsersTab() {
  const { user: me } = useAuth();
  const { items, error, reload } = useList(adminApi.users);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const close = () => {
    setDialog(null);
    setActionError(null);
  };
  const done = (msg?: string) => {
    close();
    setNotice(msg ?? null);
    reload();
  };

  const unlink = async (u: AdminUserRow) => {
    setBusy(true);
    setActionError(null);
    try {
      await adminApi.userUnlinkTelegram(u.id);
      done();
    } catch (e) {
      setActionError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="toolbar">
        <button className="btn primary" onClick={() => setDialog({ kind: "add" })}>
          Добавить пользователя
        </button>
        <span className="muted">{items ? `${items.length} пользователей` : ""}</span>
      </div>
      {error && <p className="error">{error}</p>}
      {notice && <p className="notice">{notice}</p>}
      <section className="card">
        {!items ? (
          <p className="muted">Загрузка…</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>ФИО</th>
                <th>E-mail</th>
                <th>Роль</th>
                <th>Активен</th>
                <th>Telegram</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((u) => {
                const isSelf = u.id === me?.id;
                return (
                  <tr key={u.id} className={u.isActive ? "" : "inactive"}>
                    <td>
                      <b>{u.fullName}</b>
                      {isSelf && <span className="tag">это вы</span>}
                    </td>
                    <td>{u.email}</td>
                    <td>{ROLE_LABELS[u.role]}</td>
                    <td>{u.isActive ? "да" : <span className="badge muted">отключён</span>}</td>
                    <td>
                      <TelegramTag linked={u.telegramLinked} />
                    </td>
                    <td>
                      <div className="actions-cell">
                        <button className="btn small" onClick={() => setDialog({ kind: "edit", user: u })}>
                          Изменить
                        </button>
                        <button className="btn small" onClick={() => setDialog({ kind: "password", user: u })}>
                          Сбросить пароль
                        </button>
                        {u.telegramLinked && (
                          <button className="btn small danger" onClick={() => setDialog({ kind: "unlink", user: u })}>
                            Отвязать Telegram
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
      <p className="hint">Отключённый пользователь не сможет войти; его заявки и история остаются. Telegram сотрудник подключает сам во вкладке «Профиль».</p>

      {dialog?.kind === "add" && <AddUserModal onClose={close} onDone={() => done("Пользователь создан")} />}
      {dialog?.kind === "edit" && <EditUserModal user={dialog.user} isSelf={dialog.user.id === me?.id} onClose={close} onDone={() => done()} />}
      {dialog?.kind === "password" && (
        <PasswordModal user={dialog.user} onClose={close} onDone={() => done(`Пароль для ${dialog.user.email} изменён`)} />
      )}
      {dialog?.kind === "unlink" && (
        <Modal title="Отвязать Telegram?" onClose={close}>
          <p>{dialog.user.fullName} перестанет получать уведомления в Telegram, пока не подключит бота заново из профиля.</p>
          <ConfirmActions busy={busy} error={actionError} confirmLabel="Отвязать" danger onCancel={close} onConfirm={() => void unlink(dialog.user)} />
        </Modal>
      )}
    </>
  );
}
